import { useEffect } from "react";

/**
 * Keep the screen awake for the duration of the component's mount.
 *
 * Uses the Screen Wake Lock API (`navigator.wakeLock.request("screen")`),
 * supported on iOS 16.4+, Chrome Android, Edge, and Firefox 126+. On
 * unsupported browsers (e.g. older iOS), this is a no-op — the user's
 * device may sleep as normal.
 *
 * The browser auto-releases the lock when the tab is hidden (background tab,
 * device lock screen, app switcher). We re-acquire on `visibilitychange`
 * when the tab returns to the foreground so the screen stays awake across
 * pull-down notifications / momentary backgrounding.
 *
 * Requires a secure context (HTTPS or localhost) AND a prior user gesture.
 * Both are satisfied by the in-call flow — the user just clicked Join.
 */
export function useScreenWakeLock(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    if (typeof navigator === "undefined" || !("wakeLock" in navigator)) return;

    let sentinel: WakeLockSentinel | null = null;
    let cancelled = false;

    const acquire = async () => {
      try {
        const next = await navigator.wakeLock.request("screen");
        if (cancelled) {
          await next.release().catch(() => {});
          return;
        }
        sentinel = next;
      } catch (err) {
        // Common reasons: page not visible at request time, permission
        // denied, low battery on Android. Non-fatal — just log.
        console.warn("Screen wake lock request failed:", err);
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible" && sentinel === null) {
        void acquire();
      }
    };

    void acquire();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibility);
      sentinel?.release().catch(() => {});
      sentinel = null;
    };
  }, [enabled]);
}
